import React, { useState, useEffect } from 'react';
import './App.css';
import CandidateAuth from './CandidateAuth';
import CompanyAuth from './CompanyAuth';
import Dashboard from './Dashboard';
import CompanyDashboard from './CompanyDashboard';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import AdminQuizDashboard from './AdminQuizDashboard';
import SupervisorLoginPage from './SupervisorLoginPage';
import SupervisorDashboard from './SupervisorDashboard';
import SupervisorWorkDiaryDashboard from './SupervisorWorkDiaryDashboard';
import SupervisorJobInterestPage from './SupervisorJobInterestPage';
import InstitutionLoginPage from './InstitutionLoginPage';
import InstitutionDashboard from './InstitutionDashboard';
import SPOCLoginPage from './SPOCLoginPage';
import SPOCDashboard from './SPOCDashboard';
import OfferLetterView from './OfferLetterView';
import ExternalOfferView from './ExternalOfferView';
import LandingPage from './LandingPage';
import WelcomePage from './WelcomePage';
import PrivacyPolicy from './pages/page_privacy';
import CandidateRegisterForm from './CandidateRegisterForm';
import JobInterestForm from './JobInterestForm';
import JobInterestJobPage from './JobInterestJobPage';
import SharedJobApplyPage from './SharedJobApplyPage';
import StaffingProfileCompletePage from './StaffingProfileCompletePage';
import StaffingSelfRegisterPage from './StaffingSelfRegisterPage';
import StaffingLeaveReviewPage from './StaffingLeaveReviewPage';
import NetworkCamerasPage from './NetworkCamerasPage';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { setupTokenValidation, setupFetchInterceptor } from './utils/authUtils';

const gogigGreen = '#2ad2c9';
const gogigDarkGreen = '#1bbf8a';
const gogigText = '#222';

const cardData = [
  {
    key: 'candidate',
    icon: (
      <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#22c55e"><circle cx="12" cy="8" r="4" strokeWidth="2"/><path d="M4 20c0-2.5 3.5-4 8-4s8 1.5 8 4" strokeWidth="2"/></svg>
    ),
    title: 'Candidate',
    desc: 'Find your dream job with ease',
    login: 'Login',
    signup: 'Sign Up',
  },
  {
    key: 'company',
    icon: (
      <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#22c55e"><rect x="4" y="4" width="16" height="16" rx="3" strokeWidth="2"/><path d="M9 8h1m4 0h1M9 12h1m4 0h1M9 16h1m4 0h1" strokeWidth="2"/></svg>
    ),
    title: 'Company',
    desc: 'Find perfect candidates for your team',
    login: 'Login',
    signup: 'Sign Up',
  },
  {
    key: 'admin',
    icon: (
      <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#22c55e"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path d="M12 8v4l3 3" strokeWidth="2"/></svg>
    ),
    title: 'Admin',
    desc: 'Manage the platform effectively',
    login: 'Login',
    signup: null,
  },
];

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('gogig_user') || '{}');
  } catch {
    return {};
  }
}

function hasUserIdentity(user) {
  return Boolean(user?.mobile || user?.email || user?.id);
}

function App() {
  const [modal, setModal] = useState(null);
  const [modalType, setModalType] = useState('login');
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = localStorage.getItem('gogig_token');
    return Boolean(token && hasUserIdentity(readStoredUser()));
  });
  const [userType, setUserType] = useState(() => readStoredUser()?.type || '');
  const [isAdmin, setIsAdmin] = useState(!!localStorage.getItem('gogig_admin'));
  const [isSupervisor, setIsSupervisor] = useState(!!localStorage.getItem('gogig_supervisor'));
  const [isInstitution, setIsInstitution] = useState(!!localStorage.getItem('gogig_institution'));
  const [isSPOC, setIsSPOC] = useState(!!localStorage.getItem('gogig_spoc_access'));
  const [adminView, setAdminView] = useState('main'); // 'main' | 'quiz'
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('gogig_token');
    const user = readStoredUser();
    setIsLoggedIn(Boolean(token && hasUserIdentity(user)));
    setUserType(user.type || '');
    setIsAdmin(!!localStorage.getItem('gogig_admin'));
    setIsSupervisor(!!localStorage.getItem('gogig_supervisor'));
    setIsInstitution(!!localStorage.getItem('gogig_institution'));
    const spocAccess = localStorage.getItem('gogig_spoc_access');
    console.log('App.js useEffect - spocAccess:', spocAccess);
    console.log('App.js useEffect - all localStorage keys:', Object.keys(localStorage));
    setIsSPOC(!!spocAccess);
  }, [forceUpdate]);

  // Set up token validation and auto-logout
  useEffect(() => {
    // Set up periodic token checking
    const cleanupValidation = setupTokenValidation(60000); // Check every minute
    
    // Set up global fetch interceptor for 401 errors
    const cleanupInterceptor = setupFetchInterceptor();
    
    return () => {
      cleanupValidation();
      cleanupInterceptor();
    };
  }, []);

  const handleAuthSuccess = (token, user) => {
    localStorage.setItem('gogig_token', token);
    localStorage.setItem('gogig_user', JSON.stringify(user));
    setIsLoggedIn(true);
    setUserType(user.type);
    setModal(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('gogig_token');
    localStorage.removeItem('gogig_user');
    setIsLoggedIn(false);
    setUserType('');
  };

  const handleAdminLogin = (token) => {
    localStorage.setItem('gogig_admin', 'true');
    setIsAdmin(true);
    setModal(null);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('gogig_admin');
    localStorage.removeItem('gogig_admin_access');
    localStorage.removeItem('gogig_admin_refresh');
    setIsAdmin(false);
  };

  const handleSupervisorLogin = (supervisor) => {
    localStorage.setItem('gogig_supervisor', 'true');
    localStorage.setItem('gogig_supervisor_data', JSON.stringify(supervisor));
    setIsSupervisor(true);
  };

  const handleSupervisorLogout = () => {
    localStorage.removeItem('gogig_supervisor');
    localStorage.removeItem('gogig_supervisor_access');
    localStorage.removeItem('gogig_supervisor_refresh');
    localStorage.removeItem('gogig_supervisor_data');
    setIsSupervisor(false);
  };

  const handleInstitutionLogin = (institution) => {
    localStorage.setItem('gogig_institution', 'true');
    localStorage.setItem('gogig_institution_data', JSON.stringify(institution));
    setIsInstitution(true);
  };

  const handleInstitutionLogout = () => {
    localStorage.removeItem('gogig_institution');
    localStorage.removeItem('gogig_institution_access');
    localStorage.removeItem('gogig_institution_refresh');
    localStorage.removeItem('gogig_institution_data');
    setIsInstitution(false);
  };

  const handleSPOCLogin = (spoc) => {
    // Token is already stored by SPOCLoginPage, just update the state
    console.log('handleSPOCLogin called with:', spoc);
    console.log('Current isSPOC state:', isSPOC);
    console.log('Setting isSPOC to true');
    setIsSPOC(true);
    console.log('isSPOC state updated');
    
    // Force a re-render by updating a dummy state
    setForceUpdate(prev => prev + 1);
  };

  const handleSPOCLogout = () => {
    localStorage.removeItem('gogig_spoc_access');
    localStorage.removeItem('gogig_spoc_refresh');
    localStorage.removeItem('gogig_spoc_data');
    setIsSPOC(false);
  };

  return (
    <Router>
      <Routes>
        <Route path="/offer-letter/:token" element={<OfferLetterView />} />
        <Route path="/external-offer/:token" element={<ExternalOfferView />} />
        <Route path="/staffing-profile/:token" element={<StaffingProfileCompletePage />} />
        <Route path="/staffing-register/:token" element={<StaffingSelfRegisterPage />} />
        <Route path="/staffing-leave-review/:token" element={<StaffingLeaveReviewPage mode="leave" />} />
        <Route path="/staffing-outdoor-review/:token" element={<StaffingLeaveReviewPage mode="outdoor" />} />
        <Route path="/jobs/:jobId/share" element={<SharedJobApplyPage />} />
        <Route path="/candidate-register/:supervisorId" element={<CandidateRegisterForm />} />
        <Route path="/job-interest/:supervisorId/job/:jobId" element={<JobInterestJobPage />} />
        <Route path="/job-interest/:supervisorId" element={<JobInterestForm />} />
        <Route path="/network-cameras" element={<NetworkCamerasPage />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/welcome" element={<WelcomePage onLoginSuccess={() => {
          const token = localStorage.getItem('gogig_token');
          const user = readStoredUser();
          setIsLoggedIn(Boolean(token && hasUserIdentity(user)));
          setUserType(user.type || '');
        }} />} />
        <Route path="/pages/page_privacy" element={<PrivacyPolicy />} />
        <Route 
          path="/admin" 
          element={
            isAdmin ? (
              <Navigate to="/admin/dashboard" replace />
            ) : (
              <AdminLogin onLogin={handleAdminLogin} />
            )
          } 
        />
        <Route 
          path="/admin/dashboard" 
          element={
            isAdmin ? (
              adminView === 'quiz' ? (
                <AdminQuizDashboard onBack={() => setAdminView('main')} />
              ) : (
                <AdminDashboard onLogout={handleAdminLogout} onQuiz={() => setAdminView('quiz')} />
              )
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        <Route 
          path="/supervisor" 
          element={
            isSupervisor ? (
              <Navigate to="/supervisor/dashboard" replace />
            ) : (
              <SupervisorLoginPage onLogin={handleSupervisorLogin} />
            )
          } 
        />
        <Route 
          path="/supervisor/dashboard" 
          element={
            isSupervisor ? (
              <SupervisorDashboard onLogout={handleSupervisorLogout} />
            ) : (
              <Navigate to="/supervisor" replace />
            )
          } 
        />
        <Route
          path="/supervisor/work-diaries"
          element={
            isSupervisor ? (
              <SupervisorWorkDiaryDashboard />
            ) : (
              <Navigate to="/supervisor" replace />
            )
          }
        />
        <Route
          path="/supervisor/job-interest"
          element={
            isSupervisor ? (
              <SupervisorJobInterestPage />
            ) : (
              <Navigate to="/supervisor" replace />
            )
          }
        />
        <Route 
          path="/institution" 
          element={
            isInstitution ? (
              <Navigate to="/institution/dashboard" replace />
            ) : (
              <InstitutionLoginPage onLogin={handleInstitutionLogin} />
            )
          } 
        />
        <Route 
          path="/institution/dashboard" 
          element={
            isInstitution ? (
              <InstitutionDashboard onLogout={handleInstitutionLogout} />
            ) : (
              <Navigate to="/institution" replace />
            )
          } 
        />
        <Route 
          path="/spoc" 
          element={
            isSPOC ? (
              <Navigate to="/spoc/dashboard" replace />
            ) : (
              <SPOCLoginPage onLogin={handleSPOCLogin} />
            )
          } 
        />
        <Route 
          path="/spoc/dashboard" 
          element={
            (() => {
              console.log('SPOC Dashboard route - isSPOC:', isSPOC);
              return isSPOC ? (
                <SPOCDashboard onLogout={handleSPOCLogout} />
              ) : (
                <Navigate to="/spoc" replace />
              );
            })()
          } 
        />
        <Route
          path="/dashboard"
          element={
            isLoggedIn ? (
              userType === 'company' ? (
                <Navigate to="/company/dashboard" replace />
              ) : (
                <Navigate to="/candidate/dashboard" replace />
              )
            ) : (
              <Navigate to="/welcome" replace />
            )
          }
        />
        <Route
          path="/company/dashboard"
          element={
            isLoggedIn ? (
              userType === 'company' ? (
                <CompanyDashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/candidate/dashboard" replace />
              )
            ) : (
              <Navigate to="/welcome" replace />
            )
          }
        />
        <Route
          path="/candidate/dashboard"
          element={
            isLoggedIn ? (
              userType === 'company' ? (
                <Navigate to="/company/dashboard" replace />
              ) : (
                <Dashboard onLogout={handleLogout} />
              )
            ) : (
              <Navigate to="/welcome" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
